'use client';

import { Badge } from '@ophelos/ui/components/badge';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { CheckCircle } from 'lucide-react';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import type { DebtData } from '@/types';
import type { TransactionItem } from '@/types/core-api';
import { TransactionsList } from '../_shared/TransactionsList';

interface PaymentHistoryListProps {
  account: DebtData;
  transactions: TransactionItem[];
}

export function PaymentHistoryList({ account, transactions }: PaymentHistoryListProps) {
  const { t } = useTranslation();

  return (
    <div className="flex-1 overflow-y-auto w-full flex flex-col px-4">
      <div className="max-w-xl mx-auto w-full space-y-6">
        {/* Account Summary */}

        <Card className="bg-soft border border-border-heavy shadow-none">
          <CardHeader>
            <CardTitle>{account.originalCreditor}</CardTitle>
            <CardDescription></CardDescription>
            <CardAction>
              <Badge variant="success" className="gap-1.5">
                <CheckCircle className="size-3.5" />
                {t('paymentHistory.cleared')}
              </Badge>
            </CardAction>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('paymentHistory.accountNumber')}</span>
              <span className="text-sm font-semibold">{account.externalCaseRef}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-muted-foreground">{t('paymentHistory.originalAmount')}</span>
              <CurrencyDisplay amount={account.originalAmount} currency={account.currency} className="font-semibold" />
            </div>

            <div className="flex justify-between items-center mt-2">
              <span className="text-muted-foreground">{t('paymentHistory.amountPaid')}</span>
              {/* Payments net of refunds and reversals: a discount, adjustment or credit that
                  cleared the rest was not paid, so the original amount is not what was paid. */}
              <CurrencyDisplay amount={account.paymentBreakdown.total} currency={account.currency} className="font-semibold text-green-700" />
            </div>
          </CardContent>
        </Card>

        {/* Payment History List - Using shared TransactionsList */}
        <div className="flex flex-col w-full">
          <TransactionsList
            transactions={transactions}
            title={t('transaction.transactions')}
            emptyMessage={t('transaction.noPaymentHistory')}
            currency={account.currency}
          />
        </div>
      </div>
    </div>
  );
}
